import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { OAuthPkceService } from '../../../core/auth/oauth-pkce.service';
import { AppConfigService } from '../../../core/config/app-config.service';
import type {
  AuthToken,
  CurrentProfile,
  CurrentProfileSession,
  User,
  UserSession,
} from '../../../core/auth/auth.model';

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  id_token?: string;
  expires_in: number;
  token_type: string;
}

interface UserinfoResponse {
  sub: string;
  email: string;
  name: string;
  preferred_username: string;
  user_type: string;
  email_verified: boolean;
}

interface MeResponse {
  user: {
    id: string;
    email: string;
    full_name: string;
    user_type: string;
    status: string;
  };
  session?: {
    session_id: string;
    expires_at?: string | null;
  };
}

interface SessionResponse {
  id: string;
  user_id: string;
  session_id: string;
  ip_address?: string;
  user_agent?: string;
  device_label?: string | null;
  status: string;
  started_at: string;
  last_seen_at?: string | null;
  expires_at?: string | null;
}

interface OidcDiscoveryResponse {
  issuer: string;
  authorization_endpoint: string;
  token_endpoint: string;
  userinfo_endpoint: string;
  jwks_uri: string;
  revocation_endpoint?: string;
  response_types_supported?: string[];
  subject_types_supported?: string[];
  id_token_signing_alg_values_supported?: string[];
  grant_types_supported?: string[];
  scopes_supported?: string[];
  token_endpoint_auth_methods_supported?: string[];
  claims_supported?: string[];
}

interface JwksResponse {
  keys: Array<Record<string, unknown>>;
}

@Injectable({ providedIn: 'root' })
export class AuthApi {
  private readonly http = inject(HttpClient);
  private readonly pkceService = inject(OAuthPkceService);
  private readonly appConfig = inject(AppConfigService);

  async initiateLogin(): Promise<void> {
    const codeVerifier = this.pkceService.generateCodeVerifier();
    const codeChallenge = await this.pkceService.generateCodeChallenge(codeVerifier);
    const state = this.pkceService.generateState();

    this.pkceService.storeVerifier(codeVerifier);
    this.pkceService.storeState(state);

    const params = new URLSearchParams({
      client_id: this.appConfig.oauth.clientId,
      redirect_uri: this.appConfig.oauth.redirectUri,
      response_type: 'code',
      scope: this.appConfig.oauth.scope,
      state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });

    window.location.href = `${this.appConfig.oauth.authorizeEndpoint}?${params.toString()}`;
  }

  async exchangeCode(code: string, state: string): Promise<AuthToken> {
    const storedState = this.pkceService.getState();
    if (!storedState || storedState !== state) {
      throw new Error('Invalid state parameter. Possible CSRF attack.');
    }

    const codeVerifier = this.pkceService.getVerifier();
    if (!codeVerifier) {
      throw new Error('Code verifier not found. Please try logging in again.');
    }

    const body = new HttpParams()
      .set('grant_type', 'authorization_code')
      .set('code', code)
      .set('client_id', this.appConfig.oauth.clientId)
      .set('redirect_uri', this.appConfig.oauth.redirectUri)
      .set('code_verifier', codeVerifier);

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });

    const response = await firstValueFrom(
      this.http.post<TokenResponse>(this.appConfig.oauth.tokenEndpoint, body.toString(), { headers })
    );

    this.pkceService.clearAll();

    return {
      accessToken: response.access_token,
      refreshToken: response.refresh_token,
      idToken: response.id_token,
      expiresIn: response.expires_in,
      tokenType: response.token_type,
      expiresAt: Date.now() + response.expires_in * 1000,
    };
  }

  async refreshToken(refreshToken: string): Promise<AuthToken> {
    const body = new HttpParams()
      .set('grant_type', 'refresh_token')
      .set('refresh_token', refreshToken)
      .set('client_id', this.appConfig.oauth.clientId);

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });

    const response = await firstValueFrom(
      this.http.post<TokenResponse>(this.appConfig.oauth.tokenEndpoint, body.toString(), { headers })
    );

    return {
      accessToken: response.access_token,
      refreshToken: response.refresh_token,
      idToken: response.id_token,
      expiresIn: response.expires_in,
      tokenType: response.token_type,
      expiresAt: Date.now() + response.expires_in * 1000,
    };
  }

  async getUserInfo(accessToken: string): Promise<User> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${accessToken}`,
    });

    const response = await firstValueFrom(
      this.http.get<UserinfoResponse>(this.appConfig.oauth.userinfoEndpoint, { headers })
    );

    return {
      sub: response.sub,
      email: response.email,
      name: response.name,
      preferredUsername: response.preferred_username,
      userType: response.user_type,
      emailVerified: response.email_verified,
    };
  }

  async getCurrentProfile(): Promise<CurrentProfile> {
    const response = await firstValueFrom(
      this.http.get<MeResponse>(`${this.appConfig.apiBaseUrl}/auth/me`)
    );

    return {
      user: {
        id: response.user.id,
        email: response.user.email,
        fullName: response.user.full_name,
        userType: response.user.user_type,
        status: response.user.status,
      },
      session: this.mapCurrentSession(response.session),
    };
  }

  async getUserSessions(userId: string): Promise<UserSession[]> {
    const response = await firstValueFrom(
      this.http.get<SessionResponse[]>(`${this.appConfig.apiBaseUrl}/users/${userId}/sessions`)
    );

    return response.map((session) => ({
      id: session.id,
      userId: session.user_id,
      sessionId: session.session_id,
      ipAddress: session.ip_address ?? '-',
      userAgent: session.user_agent ?? '-',
      deviceLabel: session.device_label ?? null,
      status: session.status,
      startedAt: session.started_at,
      lastSeenAt: session.last_seen_at ?? null,
      expiresAt: session.expires_at ?? null,
    }));
  }

  async revokeUserSessions(userId: string): Promise<void> {
    await firstValueFrom(
      this.http.post(`${this.appConfig.apiBaseUrl}/users/${userId}/sessions/revoke`, {})
    );
  }

  async getOidcDiscovery(): Promise<OidcDiscoveryResponse> {
    return await firstValueFrom(
      this.http.get<OidcDiscoveryResponse>(
        `${this.appConfig.oauth.issuer}/.well-known/openid-configuration`
      )
    );
  }

  async getJwks(): Promise<JwksResponse> {
    return await firstValueFrom(
      this.http.get<JwksResponse>(`${this.appConfig.oauth.issuer}/oauth2/jwks`)
    );
  }

  async logout(): Promise<void> {
    try {
      await firstValueFrom(
        this.http.post(`${this.appConfig.apiBaseUrl}/auth/logout`, {})
      );
    } catch {
      // Ignore errors — clear local state regardless
    }
  }

  private mapCurrentSession(session?: MeResponse['session']): CurrentProfileSession | null {
    if (!session?.session_id) {
      return null;
    }

    return {
      sessionId: session.session_id,
      expiresAt: session.expires_at ?? null,
    };
  }
}
